import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Bot, Lock, CheckCircle2, AlertTriangle } from "lucide-react";
import axios from "axios";
import { API, formatApiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function SetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");
  const [info, setInfo] = useState(null); // null=loading, false=invalid, obj=valid
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!token) { setInfo(false); setErr("Tautan tidak valid."); return; }
    axios.get(`${API}/auth/invite/${token}`)
      .then((r) => setInfo(r.data))
      .catch((e) => { setInfo(false); setErr(formatApiError(e.response?.data?.detail)); });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== confirm) { setErr("Konfirmasi password tidak cocok"); return; }
    if (pw.length < 6) { setErr("Password minimal 6 karakter"); return; }
    setSaving(true); setErr("");
    try {
      await axios.post(`${API}/auth/accept-invite`, { token, password: pw });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-9 h-9 rounded-xl bg-accent flex items-center justify-center"><Bot className="w-5 h-5 text-white" /></div>
          <span className="font-display text-lg font-bold">Veloce</span>
        </div>

        {info === null && <p className="text-muted-foreground text-sm">Memuat undangan...</p>}

        {info === false && (
          <div className="rounded-2xl border border-border bg-card p-6 text-center" data-testid="invite-invalid">
            <AlertTriangle className="w-8 h-8 text-destructive mx-auto mb-3" />
            <p className="font-display font-semibold">Undangan Tidak Valid</p>
            <p className="text-sm text-muted-foreground mt-1">{err || "Tautan sudah kedaluwarsa atau digunakan."}</p>
            <Button variant="outline" className="mt-4" onClick={() => navigate("/login")}>Ke Halaman Masuk</Button>
          </div>
        )}

        {info && !done && (
          <>
            <h2 className="font-display text-3xl font-bold tracking-tight">Buat Password</h2>
            <p className="text-muted-foreground mt-2 mb-6 text-sm">
              Selamat datang, <b>{info.name}</b> ({info.email}). Tetapkan password untuk mengaktifkan akun admin Anda.
            </p>
            <form onSubmit={submit} className="space-y-5">
              <div className="space-y-2">
                <Label>Password Baru</Label>
                <div className="relative">
                  <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input data-testid="set-password-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)} className="pl-10" required />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Konfirmasi Password</Label>
                <div className="relative">
                  <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input data-testid="set-password-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="pl-10" required />
                </div>
              </div>
              {err && <div className="text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2">{err}</div>}
              <Button type="submit" data-testid="set-password-submit" disabled={saving} className="w-full h-11 bg-accent hover:bg-accent/90 text-white">
                {saving ? "Menyimpan..." : "Aktifkan Akun"}
              </Button>
            </form>
          </>
        )}

        {done && (
          <div className="rounded-2xl border border-border bg-card p-6 text-center" data-testid="invite-done">
            <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-3" />
            <p className="font-display font-semibold">Akun Aktif!</p>
            <p className="text-sm text-muted-foreground mt-1">Mengalihkan ke halaman masuk...</p>
          </div>
        )}
      </motion.div>
    </div>
  );
}
